/**
 * @spec docs/specs/features/staking-position-ui.md
 * Application constants - staking tiers, chains, contract info
 */
import { env } from './env';

// Staking Tiers (based on total staked amount)
export const STAKING_TIERS = {
  NONE: { min: 0n, label: 'None', color: 'text-text-muted' },
  SILVER: { min: 1_000_000n * 10n ** 18n, label: 'Silver', color: 'text-gray-400' },
  GOLD: { min: 5_000_000n * 10n ** 18n, label: 'Gold', color: 'text-yellow-500' },
  PLATINUM: { min: 10_000_000n * 10n ** 18n, label: 'Platinum', color: 'text-blue-400' },
  PHENOM: { min: 50_000_000n * 10n ** 18n, label: 'Phenom', color: 'text-accent-purple' },
} as const;

export type StakingTierKey = keyof typeof STAKING_TIERS;

/**
 * Get tier based on staked amount
 */
export function getTierFromAmount(amount: bigint): StakingTierKey {
  if (amount >= STAKING_TIERS.PHENOM.min) return 'PHENOM';
  if (amount >= STAKING_TIERS.PLATINUM.min) return 'PLATINUM';
  if (amount >= STAKING_TIERS.GOLD.min) return 'GOLD';
  if (amount >= STAKING_TIERS.SILVER.min) return 'SILVER';
  return 'NONE';
}

// Position Status
export const POSITION_STATUS = {
  ESCALATOR: 'Escalator',
  HARD_LOCK: 'Hard Lock',
  COOLDOWN: 'Cooldown',
} as const;

export type PositionStatus = (typeof POSITION_STATUS)[keyof typeof POSITION_STATUS];

// Chain Information
export const CHAINS = {
  BASE: {
    id: 8453,
    name: 'Base',
    rpcUrl: env.baseRpcUrl,
    explorerUrl: env.baseExplorerUrl,
    explorerName: 'Basescan',
    nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
  },
  POLYGON: {
    id: 137,
    name: 'Polygon',
    rpcUrl: env.polygonRpcUrl,
    explorerUrl: env.polygonExplorerUrl,
    explorerName: 'Polygonscan',
    nativeCurrency: { name: 'MATIC', symbol: 'MATIC', decimals: 18 },
  },
} as const;

// Contract Addresses
export const CONTRACTS = {
  BASE: {
    PHNM_STAKING: env.contracts.stakingAddress,
    PHNM_CLAIM: env.contracts.claimAddress,
  },
  POLYGON: {
    USDT_REDEMPTION: env.contracts.usdtRedemptionAddress,
  },
} as const;

// Token Decimals
export const DECIMALS = {
  PHNM: 18,
  USDT: env.usdtDecimals, // Environment-specific: 18 for dev/testnet, 6 for production
} as const;

// Time Constants
export const TIME = {
  SECOND: 1000,
  MINUTE: 60 * 1000,
  HOUR: 60 * 60 * 1000,
  DAY: 24 * 60 * 60 * 1000,
} as const;

// API Polling Intervals (milliseconds)
export const POLLING_INTERVALS = {
  POSITIONS: 30 * TIME.SECOND, // 30 seconds
  STATS: 60 * TIME.SECOND, // 1 minute
  REWARDS: 60 * TIME.SECOND, // 1 minute
} as const;
